import React, { useState } from 'react';
import { Search, ArrowUpDown, Mail, MessageCircle, X, Undo } from 'lucide-react';
import { Button } from '../../../../ui/Button';
import { usePotentialClients } from '../../../../../hooks/usePotentialClients';
import { usePropertyFormContext } from '../../contexts/PropertyFormContext';
import { findMatchingClients } from '../../../../../utils/matching';

interface MatchingProps {
  onNext: () => void;
}

export const Matching: React.FC<MatchingProps> = ({ onNext }) => {
  const { clients, isLoading } = usePotentialClients();
  const { formData } = usePropertyFormContext();
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [rejectedClients, setRejectedClients] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'score' | 'date'>('score');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState('');

  // Simuler une propriété à partir des données du formulaire
  const property = {
    id: 'temp',
    type: formData.propertyType,
    status: formData.transactionType === 'sale' ? 'for-sale' : 'for-rent',
    price: parseFloat(formData.salePrice),
    features: {
      area: parseFloat(formData.livingArea || '0'),
      rooms: parseInt(formData.roomCount || '0', 10),
      hasParking: formData.hasParking || false,
      hasElevator: formData.hasElevator || false,
    }
  };

  // Trouver les clients correspondants
  const matches = findMatchingClients(property as any, clients);

  // Filtrer par recherche
  const filteredMatches = matches.filter(match => {
    if (rejectedClients.includes(match.client.id)) return false;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      match.client.firstName.toLowerCase().includes(searchLower) ||
      match.client.lastName.toLowerCase().includes(searchLower) ||
      match.client.email.toLowerCase().includes(searchLower) ||
      match.client.phone.includes(searchTerm)
    );
  });

  // Trier les résultats
  const sortedMatches = [...filteredMatches].sort((a, b) => {
    let comparison = 0;
    if (sortBy === 'score') {
      comparison = b.score - a.score;
    } else {
      comparison = b.client.lastContact.getTime() - a.client.lastContact.getTime();
    }
    return sortOrder === 'asc' ? -comparison : comparison;
  });

  const handleSort = (field: 'score' | 'date') => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleRejectClient = (clientId: string) => {
    setRejectedClients(prev => [...prev, clientId]);
  };

  const handleUndoReject = (clientId: string) => {
    setRejectedClients(prev => prev.filter(id => id !== clientId));
  };

  const handleEmailClient = (client: any) => {
    const subject = encodeURIComponent(`Nouveau bien immobilier correspondant à vos critères`);
    const body = encodeURIComponent(
      `Bonjour ${client.firstName} ${client.lastName},\n\n` +
      `Nous avons un nouveau bien qui pourrait vous intéresser :\n\n` +
      `${formData.propertyType}\n` +
      `${formData.livingArea} m² - ${formData.roomCount} pièces\n` +
      `Prix : ${formData.salePrice} DA\n\n` +
      `N'hésitez pas à nous contacter pour plus d'informations.`
    );
    window.location.href = `mailto:${client.email}?subject=${subject}&body=${body}`;
  };

  const handleWhatsAppClient = (client: any) => {
    const message = encodeURIComponent(
      `Bonjour ${client.firstName} ${client.lastName},\n\n` +
      `Nous avons un nouveau bien qui pourrait vous intéresser :\n\n` +
      `${formData.propertyType}\n` +
      `${formData.livingArea} m² - ${formData.roomCount} pièces\n` +
      `Prix : ${formData.salePrice} DA\n\n` +
      `N'hésitez pas à nous contacter pour plus d'informations.`
    );
    // Formater le numéro de téléphone pour WhatsApp (supprimer les espaces et le +)
    const phone = client.phone.replace(/\s+/g, '').replace('+', '');
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-500 bg-green-50 dark:bg-green-900/20';
    if (score >= 50) return 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20';
    return 'text-orange-500 bg-orange-50 dark:bg-orange-900/20';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-500 dark:text-gray-400">
            Recherche des clients potentiels...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="relative max-w-xs flex-1">
          <input
            type="text"
            placeholder="Rechercher un client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        </div>
        <div className="flex items-center gap-4">
          <Button
            variant="secondary"
            onClick={() => handleSort('score')}
            className="flex items-center gap-2"
          >
            Score
            <ArrowUpDown className="h-4 w-4" />
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleSort('date')}
            className="flex items-center gap-2"
          >
            Date
            <ArrowUpDown className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Client</th>
              <th scope="col" className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Contact</th>
              <th scope="col" className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Critères</th>
              <th scope="col" className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Score</th>
              <th scope="col" className="px-4 py-3 text-right text-sm font-semibold text-gray-900 dark:text-white">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {sortedMatches.map(({ client, score, matches: criteriaMatches }) => (
              <tr 
                key={client.id} 
                className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${
                  rejectedClients.includes(client.id) ? 'opacity-50' : ''
                }`}
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900 dark:text-white">
                    {client.civility} {client.firstName} {client.lastName}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Commercial : {client.agent}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm text-gray-900 dark:text-white">{client.phone}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{client.email}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {criteriaMatches.filter(m => m.matches).map((match, idx) => (
                      <span 
                        key={idx}
                        className="inline-flex items-center rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                      >
                        {match.criteria}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getScoreColor(score)}`}>
                    {Math.round(score)}%
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleEmailClient(client)}
                      title="Envoyer par email"
                    >
                      <Mail className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleWhatsAppClient(client)}
                      title="Envoyer par WhatsApp"
                    >
                      <MessageCircle className="h-4 w-4" />
                    </Button>
                    {rejectedClients.includes(client.id) ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleUndoReject(client.id)}
                        title="Annuler le rejet"
                      >
                        <Undo className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleRejectClient(client.id)}
                        title="Ne convient pas"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <Button onClick={onNext}>
          Terminer
        </Button>
      </div>
    </div>
  );
};